// @ts-nocheck -- TypeScript migration in progress: bulk-renamed from .js and not
// yet typed. Remove this directive and add real types when converting this
// module. Backlog: grep -rl "@ts-nocheck" src. See docs/typescript-migration.md.
import { combineEngineMetricResults, labelEngineMetricResult } from "./utils.js";
import {
  averageValues,
  extentFromValues,
} from "../core/math.js";
import {
  cylinderKeys,
  cylinderNumberFromKey,
} from "../core/schema.js";
import {
  engineCylinderKeys,
  engineInfoForSourceNumber,
  engineScopedCylinderLabel,
  projectFlightDataForEngine,
} from "../core/engines.js";
import {
  clampScore,
  withEngineCoverageConfidence,
  continuousAnalysisRuns,
  evidenceIntervalSeconds,
  elapsedAnchoredSampleForAnalysis,
  median,
  peerAverage,
  pointCylinderValues,
  rollingMedianStep,
  thermalEngineWorkPoint as engineWorkPoint,
} from "./utils.js";
import {
  chtEgtPairingParameters,
} from "./detectors/cht-egt-pairing.js";

function rangeOf(values) {
  const extent = extentFromValues(values);
  return extent ? extent.max - extent.min : null;
}

// Size of the leading/trailing segment used to measure start-vs-end change inside
// a window (clamped so very short windows still keep a couple of samples per end).
function segmentSize(length) {
  const config = chtEgtPairingParameters.windowChange;
  return Math.max(config.minSegmentSamples, Math.floor(length / config.segmentDivisor));
}

// Power/mixture movement at or above this normalized score (0..1) is treated as a
// confounder: the movement is itself a plausible explanation for the EGT/CHT swing,
// so a same-cylinder cue measured across it is reported with less confidence.
export const combustionPowerConfoundLevel = chtEgtPairingParameters.powerMovement.moderateLevel;

// Interpolating median (q=0.5). Used only for aggregating ACROSS cylinders, where
// the sample count is small and even (4- and 6-cylinder engines): the plain
// median picks the upper-middle element, biasing the peer/common baseline high.
function interpolatedMedian(values) {
  const sorted = values.filter(Number.isFinite).sort((a, b) => a - b);
  if (!sorted.length) return null;
  const index = (sorted.length - 1) / 2;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  return lower === upper ? sorted[lower] : (sorted[lower] + sorted[upper]) / 2;
}

function isStableEgtWindow(window, egtReference = null) {
  const config = chtEgtPairingParameters.stableWindow;
  if (window.length < config.minSamples) return false;
  const runs = continuousAnalysisRuns(window, () => true, chtEgtPairingParameters.coverage);
  if (runs.length !== 1 || runs[0].length !== window.length) return false;
  const first = window[0];
  const last = window.at(-1);
  const duration = (last.elapsedSeconds ?? window.length - 1) - (first.elapsedSeconds ?? 0);
  if (duration < config.minDurationSeconds) return false;

  const egtAverages = window.map((point) => averageValues(pointCylinderValues(point))).filter(Number.isFinite);
  if (egtAverages.length < window.length * config.minEgtCoverageShare) return false;
  const averageEgt = averageValues(egtAverages);
  // The minimum-EGT gate confirms the engine is actually producing combustion heat
  // (not shut down or windmilling). A fixed 850 F floor wrongly skips every window on
  // engines/probes that simply cruise cooler, so scale it down toward this log's own
  // working EGT — but only ever relax it, never raise it above the absolute floor.
  const minAverageEgt = Number.isFinite(egtReference)
    ? Math.min(config.minAverageEgtF, Math.max(config.minAverageEgtFloorF, egtReference * config.minAverageEgtShare))
    : config.minAverageEgtF;
  if (!Number.isFinite(averageEgt) || averageEgt < minAverageEgt) return false;
  if ((rangeOf(egtAverages) ?? 0) > config.maxEgtRangeF) return false;
  const driftThird = Math.max(1, Math.floor(egtAverages.length / 3));
  const driftStart = median(egtAverages.slice(0, driftThird));
  const driftEnd = median(egtAverages.slice(-driftThird));
  if (Number.isFinite(driftStart) && Number.isFinite(driftEnd)
    && Math.abs(driftEnd - driftStart) / Math.max(duration, 1) > config.maxEgtDriftFps) return false;

  const rpmValues = window.map((point) => point.rpm).filter(Number.isFinite);
  if (rpmValues.length >= window.length * config.powerCoverageShare && (rangeOf(rpmValues) ?? 0) > Math.max(config.maxRpmRange, (median(rpmValues) ?? 0) * config.maxRpmRangeShare)) return false;

  const mapValues = window.map((point) => point.map).filter(Number.isFinite);
  if (mapValues.length >= window.length * config.powerCoverageShare && (rangeOf(mapValues) ?? 0) > config.maxMapRangeInHg) return false;

  const fuelValues = window.map((point) => point.fuelFlow).filter(Number.isFinite);
  if (fuelValues.length >= window.length * config.powerCoverageShare && (rangeOf(fuelValues) ?? 0) > Math.max(config.maxFuelFlowRangeGph, (median(fuelValues) ?? 0) * config.maxFuelFlowRangeShare)) return false;

  return true;
}

function findStableEgtWindows(data, windowSeconds = chtEgtPairingParameters.stableWindow.windowSeconds) {
  const config = chtEgtPairingParameters.stableWindow;
  const step = rollingMedianStep(data);
  const sampleCount = Math.max(config.minWindowSamples, Math.round(windowSeconds / step));
  const stride = Math.max(config.minStrideSamples, Math.round(sampleCount / config.strideDivisor));
  const egtReference = median(
    data.filter(engineWorkPoint).map((point) => averageValues(pointCylinderValues(point))).filter(Number.isFinite),
  );
  const windows = [];
  for (let start = 0; start + sampleCount <= data.length; start += stride) {
    const window = data.slice(start, start + sampleCount);
    if (isStableEgtWindow(window, egtReference)) windows.push({ start, end: start + sampleCount - 1, points: window, windowSeconds });
  }
  return windows;
}

function endMedianChange(points, key) {
  return pairedWindowChange(points, (point) => point[key]);
}

function pairedWindowChange(points, valueForPoint) {
  const config = chtEgtPairingParameters.windowChange;
  const values = points.map(valueForPoint).filter(Number.isFinite);
  if (values.length < Math.max(config.minSamples, points.length * config.minSampleShare)) return null;
  const third = segmentSize(values.length);
  const start = median(values.slice(0, third));
  const end = median(values.slice(-third));
  return Number.isFinite(start) && Number.isFinite(end) ? end - start : null;
}

function medianForWindow(points, key) {
  return median(points.map((point) => point[key]).filter(Number.isFinite));
}

function pointAverageForKeys(point, keys) {
  return averageValues(keys.map((key) => point[key]).filter(Number.isFinite));
}

function pointMedianForKeys(point, keys) {
  return interpolatedMedian(keys.map((key) => point[key]).filter(Number.isFinite));
}

function peerMedian(point, keys, excludedKey) {
  return interpolatedMedian(keys.filter((key) => key !== excludedKey).map((key) => point?.[key]).filter(Number.isFinite));
}

function commonWindowChange(window, keys, options = {}) {
  const pointCenter = options.robust ? pointMedianForKeys : pointAverageForKeys;
  return pairedWindowChange(window, (point) => pointCenter(point, keys));
}

function relativeWindowChange(window, key, peerKeys, options = {}) {
  return pairedWindowChange(window, (point) => {
    const value = point[key];
    const peer = options.robust ? peerMedian(point, peerKeys, key) : peerAverage(point, peerKeys, key);
    return Number.isFinite(value) && Number.isFinite(peer) ? value - peer : null;
  });
}

function windowEndMedian(window, key) {
  const third = segmentSize(window.length);
  return median(window.slice(-third).map((point) => point[key]).filter(Number.isFinite));
}

function windowPeerEndMedian(window, key, peerKeys, options = {}) {
  const third = segmentSize(window.length);
  return median(window.slice(-third).map((point) => (
    options.robust ? peerMedian(point, peerKeys, key) : peerAverage(point, peerKeys, key)
  )).filter(Number.isFinite));
}

function windowPowerMovement(window) {
  return {
    fuelChange: endMedianChange(window, "fuelFlow"),
    rpmChange: endMedianChange(window, "rpm"),
    mapChange: endMedianChange(window, "map"),
  };
}

const powerMovementThresholds = chtEgtPairingParameters.powerMovement.thresholds;

function powerMovementScore(value, thresholds) {
  if (!Number.isFinite(value)) return 0;
  const magnitude = Math.abs(value);
  if (magnitude <= thresholds.moderate) return 0;
  if (magnitude >= thresholds.strong) return 1;
  return (magnitude - thresholds.moderate) / Math.max(thresholds.strong - thresholds.moderate, chtEgtPairingParameters.powerMovement.minInterpolationWidth);
}

function formatSignedPowerMovement(value, thresholds) {
  if (!Number.isFinite(value)) return "";
  const sign = value > 0 ? "+" : "";
  return `${sign}${value.toFixed(thresholds.digits)}${thresholds.unit}`;
}

function formatPowerMovementDetail(check) {
  if (!check || !Number.isFinite(check.value)) return "";
  if (check.kind === "range") return `${Math.abs(check.value).toFixed(check.thresholds.digits)}${check.thresholds.unit}`;
  return formatSignedPowerMovement(check.value, check.thresholds);
}

function rangeForWindow(points, key) {
  return rangeOf(points.map((point) => point[key]));
}

function combustionPowerContext(data, windowRef, window, movement) {
  const config = chtEgtPairingParameters.powerMovement;
  const third = segmentSize(window.length);
  const firstThird = window.slice(0, third);
  const sampleCount = Math.max(config.priorSampleFloor, Math.min(window.length, Math.round(window.length * config.priorSampleShare)));
  const prior = data
    .slice(Math.max(0, windowRef.start - sampleCount), windowRef.start)
    .filter(engineWorkPoint);
  const checks = [
    ["fuelFlow", movement.fuelChange],
    ["rpm", movement.rpmChange],
    ["map", movement.mapChange],
  ].flatMap(([key, insideChange]) => {
    const thresholds = powerMovementThresholds[key];
    const priorMedian = medianForWindow(prior, key);
    const startMedian = medianForWindow(firstThird, key);
    const leadInChange = Number.isFinite(priorMedian) && Number.isFinite(startMedian) ? startMedian - priorMedian : null;
    const windowRange = rangeForWindow(window, key);
    return [
      { key, kind: "inside", value: insideChange, score: powerMovementScore(insideChange, thresholds), thresholds },
      { key, kind: "lead-in", value: leadInChange, score: powerMovementScore(leadInChange, thresholds) * config.leadInWeight, thresholds },
      { key, kind: "range", value: windowRange, score: powerMovementScore(windowRange, {
        ...thresholds,
        moderate: thresholds.moderate * config.rangeModerateScale,
        strong: thresholds.strong * config.rangeStrongScale,
      }) * config.rangeWeight, thresholds },
    ];
  });
  const strongest = checks
    .filter((check) => check.score > 0)
    .sort((a, b) => b.score - a.score)[0] ?? null;
  const score = Math.min(1, strongest?.score ?? 0);
  const detail = strongest
    ? `${strongest.thresholds.label} ${strongest.kind === "range" ? "varied" : "changed"} ${formatPowerMovementDetail(strongest)} ${strongest.kind === "lead-in" ? "near the window start" : "inside the window"}`
    : "";
  return {
    score,
    level: score >= config.strongLevel ? "strong" : score >= config.moderateLevel ? "moderate" : "low",
    detail,
    strongest,
  };
}

function medianCommonForWindow(points, keys, options = {}) {
  const pointCenter = options.robust ? pointMedianForKeys : pointAverageForKeys;
  return median(points.map((point) => pointCenter(point, keys)).filter(Number.isFinite));
}

function fuelThermalObservationScore(observation, thresholds) {
  const config = chtEgtPairingParameters.fuelThermal;
  if (!Number.isFinite(observation.fuelChange) || !Number.isFinite(observation.egtChange)) return 0;
  const fuelMagnitude = Math.abs(observation.fuelChange);
  const egtMagnitude = Math.abs(observation.egtChange);
  const fuelScore = fuelMagnitude <= thresholds.moderate
    ? 0
    : Math.min(1, (fuelMagnitude - thresholds.moderate) / Math.max(thresholds.strong - thresholds.moderate, chtEgtPairingParameters.powerMovement.minInterpolationWidth));
  const egtScore = egtMagnitude <= config.egtModerateF ? 0 : Math.min(1, (egtMagnitude - config.egtModerateF) / config.egtStrongSpanF);
  const matchedDirection = (observation.fuelChange < 0 && observation.egtChange > 0)
    || (observation.fuelChange > 0 && observation.egtChange < 0);
  return matchedDirection ? Math.min(1, fuelScore * config.fuelWeight + egtScore * config.egtWeight) : 0;
}

function fuelFlowThermalContext(data, windowRef, window, egtKeys) {
  const config = chtEgtPairingParameters.fuelThermal;
  const fuelMedian = medianForWindow(window, "fuelFlow");
  if (!Number.isFinite(fuelMedian)) return { score: 0, detail: "", observations: [] };

  const fuelThresholds = {
    moderate: Math.max(config.moderateFuelFloorGph, fuelMedian * config.moderateFuelShare),
    strong: Math.max(config.strongFuelFloorGph, fuelMedian * config.strongFuelShare),
  };
  const third = segmentSize(window.length);
  const firstThird = window.slice(0, third);
  const lastThird = window.slice(-third);
  const contextCount = Math.max(chtEgtPairingParameters.powerMovement.priorSampleFloor, Math.min(window.length, Math.round(window.length * chtEgtPairingParameters.powerMovement.priorSampleShare)));
  const prior = data
    .slice(Math.max(0, windowRef.start - contextCount), windowRef.start)
    .filter(engineWorkPoint);
  const after = data
    .slice(windowRef.end + 1, Math.min(data.length, windowRef.end + 1 + contextCount))
    .filter(engineWorkPoint);

  const windowEgtChange = commonWindowChange(window, egtKeys, { robust: true });
  const startFuel = medianForWindow(firstThird, "fuelFlow");
  const endFuel = medianForWindow(lastThird, "fuelFlow");
  const priorFuel = medianForWindow(prior, "fuelFlow");
  const afterFuel = medianForWindow(after, "fuelFlow");
  const endEgt = medianCommonForWindow(lastThird, egtKeys, { robust: true });
  const afterEgt = medianCommonForWindow(after, egtKeys, { robust: true });
  const observations = [
    {
      kind: "inside",
      phrase: "inside the window",
      fuelChange: endMedianChange(window, "fuelFlow"),
      egtChange: windowEgtChange,
    },
    {
      // Lead-in tests whether a fuel change going INTO the window (prior -> window
      // start) explains the EGT movement seen inside it, so egtChange deliberately
      // reuses the in-window EGT change rather than measuring EGT across the boundary.
      kind: "lead-in",
      phrase: "near the window start",
      fuelChange: Number.isFinite(priorFuel) && Number.isFinite(startFuel) ? startFuel - priorFuel : null,
      egtChange: windowEgtChange,
    },
    {
      kind: "follow-through",
      phrase: "after the window",
      fuelChange: Number.isFinite(afterFuel) && Number.isFinite(endFuel) ? afterFuel - endFuel : null,
      egtChange: Number.isFinite(afterEgt) && Number.isFinite(endEgt) ? afterEgt - endEgt : null,
    },
  ].map((observation) => ({
    ...observation,
    score: fuelThermalObservationScore(observation, fuelThresholds),
  })).filter((observation) => observation.score > 0);

  const top = observations.sort((a, b) => b.score - a.score)[0] ?? null;
  if (!top) return { score: 0, detail: "", observations };

  const fuelVerb = top.fuelChange < 0 ? "decreased" : "increased";
  const egtVerb = top.egtChange < 0 ? "fell" : "rose";
  const detail = `fuel flow ${fuelVerb} ${Math.abs(top.fuelChange).toFixed(1)} GPH ${top.phrase} while median EGT ${egtVerb} ${Math.round(Math.abs(top.egtChange))} F`;
  return {
    score: top.score,
    detail,
    top,
    observations,
  };
}

function fuelFlowMovementSplitContext(powerMovement, commonEgtChange, commonChtChange) {
  const config = chtEgtPairingParameters.fuelThermal;
  const strongest = powerMovement?.strongest ?? null;
  if (strongest?.key !== "fuelFlow" || (strongest.score ?? 0) < config.movementExplainScore) {
    return { score: 0, detail: "", top: null, observations: [] };
  }
  const movementDetail = powerMovement?.detail || `fuel flow moved ${formatPowerMovementDetail(strongest)} ${strongest.kind === "lead-in" ? "near the window start" : "inside the window"}`;
  const egtText = Number.isFinite(commonEgtChange)
    ? ` while median EGT ${commonEgtChange >= 0 ? "rose" : "fell"} ${Math.round(Math.abs(commonEgtChange))} F`
    : "";
  const chtText = Number.isFinite(commonChtChange)
    ? ` and median CHT ${commonChtChange >= 0 ? "rose" : "fell"} ${Math.round(Math.abs(commonChtChange))} F`
    : "";
  const observation = {
    kind: `fuel-flow-${strongest.kind}`,
    phrase: strongest.kind === "lead-in" ? "near the window start" : "inside the window",
    fuelChange: strongest.value,
    egtChange: commonEgtChange,
    chtChange: commonChtChange,
    score: strongest.score,
  };
  return {
    score: strongest.score,
    detail: `${movementDetail}${egtText}${chtText}`,
    top: observation,
    observations: [observation],
  };
}

function strongerFuelFlowContext(primary, fallback) {
  return (fallback?.score ?? 0) > (primary?.score ?? 0) ? fallback : primary;
}

function isWindowPowerStable(movement) {
  const checks = [];
  if (Number.isFinite(movement.fuelChange)) checks.push(Math.abs(movement.fuelChange) <= powerMovementThresholds.fuelFlow.moderate);
  if (Number.isFinite(movement.rpmChange)) checks.push(Math.abs(movement.rpmChange) <= powerMovementThresholds.rpm.moderate);
  if (Number.isFinite(movement.mapChange)) checks.push(Math.abs(movement.mapChange) <= powerMovementThresholds.map.moderate);
  const strongMovement = (
    (Number.isFinite(movement.fuelChange) && Math.abs(movement.fuelChange) > powerMovementThresholds.fuelFlow.strong)
    || (Number.isFinite(movement.rpmChange) && Math.abs(movement.rpmChange) > powerMovementThresholds.rpm.strong)
    || (Number.isFinite(movement.mapChange) && Math.abs(movement.mapChange) > powerMovementThresholds.map.strong)
  );
  if (strongMovement) return false;
  return checks.length ? checks.some(Boolean) : true;
}

export function combustionEvidenceMeta(candidate) {
  const pieces = [];
  if (Number.isFinite(candidate.cylinder)) pieces.push(`cyl ${candidate.cylinder}`);
  if (Number.isFinite(candidate.egtChange)) pieces.push(`EGT ${candidate.egtChange > 0 ? "+" : ""}${Math.round(candidate.egtChange)} F`);
  if (Number.isFinite(candidate.chtChange)) pieces.push(`CHT ${candidate.chtChange > 0 ? "+" : ""}${Math.round(candidate.chtChange)} F`);
  if (Number.isFinite(candidate.commonEgtChange)) pieces.push(`median EGT ${candidate.commonEgtChange > 0 ? "+" : ""}${Math.round(candidate.commonEgtChange)} F`);
  if (Number.isFinite(candidate.commonChtChange)) pieces.push(`median CHT ${candidate.commonChtChange > 0 ? "+" : ""}${Math.round(candidate.commonChtChange)} F`);
  if (candidate.fuelFlowThermal?.detail) pieces.push(candidate.fuelFlowThermal.detail);
  if ((candidate.powerMovementScore ?? 0) >= combustionPowerConfoundLevel
    && candidate.powerMovement?.detail
    && !String(candidate.fuelFlowThermal?.detail ?? "").includes(candidate.powerMovement.detail)) {
    pieces.push(candidate.powerMovement.detail);
  }
  return pieces.join(" · ");
}

function combustionLabel(type) {
  return {
    coldCylinder: "Relative cold-cylinder pattern",
    ignitionCylinder: "Single-cylinder CHT/EGT split",
    globalIgnition: "Global CHT/EGT split",
    globalMixture: "Fuel-flow-correlated CHT/EGT split",
    injectorRop: "Same-cylinder EGT/CHT rise",
    injectorLop: "Same-cylinder EGT/CHT drop",
  }[type] ?? "CHT/EGT Pairing";
}

function dedupeCombustionCandidates(candidates) {
  // Overlapping stable windows re-detect the same physical condition, so collapse
  // to one entry per (type, cylinder) — keeping the strongest reading and counting
  // how many windows showed it — instead of emitting a near-duplicate per window.
  const groups = new Map();
  candidates.forEach((candidate) => {
    const key = `${candidate.type}:${Number.isFinite(candidate.cylinder) ? candidate.cylinder : "global"}`;
    const existing = groups.get(key);
    if (!existing) {
      groups.set(key, { ...candidate, windowCount: 1 });
      return;
    }
    existing.windowCount += 1;
    if (candidate.suspicion > existing.suspicion) {
      groups.set(key, { ...candidate, windowCount: existing.windowCount });
    }
  });
  let deduped = [...groups.values()];
  // A cold-cylinder reading and a same-cylinder EGT/CHT drop (injectorLop) are the
  // same event caught at different stages of the drop; the cold reading is the
  // stronger explanation, so drop the injectorLop twin for that cylinder.
  const coldCylinders = new Set(
    deduped
      .filter((candidate) => candidate.type === "coldCylinder" && Number.isFinite(candidate.cylinder))
      .map((candidate) => candidate.cylinder),
  );
  if (coldCylinders.size) {
    deduped = deduped.filter((candidate) => !(candidate.type === "injectorLop" && coldCylinders.has(candidate.cylinder)));
  }
  return deduped.sort((a, b) => b.suspicion - a.suspicion);
}

// Count elapsed support once even when the detector's sliding windows overlap.
function summarizeSteadyEvidence(windows, egtKeys) {
  const interval = window => ({ start: window.points[0]?.elapsedSeconds, end: window.points.at(-1)?.elapsedSeconds });
  const steadySeconds = evidenceIntervalSeconds(windows.map(interval));
  const spreads = windows.map((windowRef) => {
    const tail = windowRef.points.slice(-segmentSize(windowRef.points.length));
    const cylinderEndEgts = egtKeys
      .map((key) => median(tail.map((point) => point[key]).filter(Number.isFinite)))
      .filter(Number.isFinite);
    return rangeOf(cylinderEndEgts);
  }).filter(Number.isFinite);
  return {
    windows: windows.length,
    steadySeconds: Math.round(steadySeconds),
    egtSpreadF: spreads.length ? Math.round(interpolatedMedian(spreads)) : null,
    coverageByCylinder: egtKeys.map(key => ({ key, seconds: evidenceIntervalSeconds(windows
      .filter(window => window.pairedKeys.includes(key)).map(interval)) })),
  };
}

// --- Settled-state gates (see chtEgtPairingParameters.settledContext) ---

function valuesInRange(data, a, b, valueForPoint) {
  const values = [];
  for (const point of data) {
    const t = point.elapsedSeconds;
    if (!Number.isFinite(t) || t < a) continue;
    if (t > b) break;
    const value = valueForPoint(point);
    if (Number.isFinite(value)) values.push(value);
  }
  return values;
}

// Peer-relative EGT for a single cylinder, or the fleet median for global cues.
function relativeEgtAt(data, a, b, egtKey, egtKeys) {
  return median(valuesInRange(data, a, b, (point) => {
    if (!egtKey) return interpolatedMedian(egtKeys.map((key) => point[key]).filter(Number.isFinite));
    const own = point[egtKey];
    const peers = egtKeys.filter((key) => key !== egtKey).map((key) => point[key]).filter(Number.isFinite);
    return Number.isFinite(own) && peers.length ? own - interpolatedMedian(peers) : null;
  }));
}

// A window only counts as evidence when the minutes leading into it were also
// mixture/power quiet (CHT's thermal mass means an earlier mixture event still
// distorts the window) and it is not adjacent to ground/takeoff/landing work.
function windowHasSettledContext(data, windowRef) {
  const config = chtEgtPairingParameters.settledContext;
  const startTime = windowRef.points[0]?.elapsedSeconds;
  if (!Number.isFinite(startTime)) return false;
  const leadInStart = startTime - config.leadInSeconds;
  const leadIn = data.filter((point) => Number.isFinite(point.elapsedSeconds)
    && point.elapsedSeconds >= leadInStart && point.elapsedSeconds < startTime);
  if (leadInStart < (data[0]?.elapsedSeconds ?? 0)) return false; // log starts inside the horizon
  if (!leadIn.length || leadIn[0].elapsedSeconds - leadInStart > chtEgtPairingParameters.coverage.maxGapSeconds) return false;
  const support = [...leadIn, ...windowRef.points];
  if (!["rpm", "map", "fuelFlow"].some(key => support.every(point => Number.isFinite(point[key])))) return false;
  const runs = continuousAnalysisRuns(support, () => true, chtEgtPairingParameters.coverage);
  if (runs.length !== 1 || runs[0].length !== support.length) return false;
  const phases = new Set([...leadIn, ...windowRef.points].map((point) => point.phase).filter(Boolean));
  if ([...phases].some((phase) => config.excludedPhases.includes(phase))) return false;
  const gate = (key, maxRange) => {
    const values = leadIn.map((point) => point[key]).filter(Number.isFinite);
    if (values.length < leadIn.length * config.leadInMinCoverageShare) return true; // channel not logged
    const extent = extentFromValues(values);
    return !extent || extent.max - extent.min <= maxRange;
  };
  return gate("fuelFlow", config.leadInMaxFuelFlowRangeGph)
    && gate("rpm", config.leadInMaxRpmRange)
    && gate("map", config.leadInMaxMapRangeInHg);
}

// Settle-persistence verdict: a real combustion change keeps the relative EGT
// shifted once temperatures settle after the window; thermal lag re-converges.
function candidateSettleVerdict(data, candidate, egtKeys) {
  const config = chtEgtPairingParameters.settledContext;
  const egtKey = Number.isFinite(candidate.cylinder) ? candidate.egtKey : null;
  const baseline = relativeEgtAt(data, candidate.startTime - 90, candidate.startTime, egtKey, egtKeys);
  const endRel = relativeEgtAt(data, candidate.endTime - 20, candidate.endTime, egtKey, egtKeys);
  const settleValues = valuesInRange(
    data, candidate.endTime + config.settleStartSeconds, candidate.endTime + config.settleEndSeconds,
    (point) => {
      if (!egtKey) return interpolatedMedian(egtKeys.map((key) => point[key]).filter(Number.isFinite));
      const own = point[egtKey];
      const peers = egtKeys.filter((key) => key !== egtKey).map((key) => point[key]).filter(Number.isFinite);
      return Number.isFinite(own) && peers.length ? own - interpolatedMedian(peers) : null;
    },
  );
  if (settleValues.length < config.settleMinSamples) return { verdict: "unconfirmed" };
  const settledRel = median(settleValues);
  if (![baseline, endRel, settledRel].every(Number.isFinite)) return { verdict: "unconfirmed" };
  const inShift = endRel - baseline;
  const settledShift = settledRel - baseline;
  if (Math.abs(inShift) <= 4) return { verdict: "persisted", settledShiftF: Math.round(settledShift) };
  const reconverged = Math.abs(settledShift) < Math.max(config.reconvergeAbsoluteF, config.reconvergeShare * Math.abs(inShift));
  return { verdict: reconverged ? "reconverged" : "persisted", settledShiftF: Math.round(settledShift) };
}

// Transient-vs-systemic classification for the top cue. Overlapping stride
// windows re-detect the same physical event, so "how many windows flagged it"
// overstates repetition; merging the matching windows into episodes (bridging
// pauses shorter than episodeGapSeconds) recovers "how many separate times the
// cue actually appeared". Systemic = the same cylinder shows the same signature
// in two or more distinct episodes, or one episode persists for several
// minutes. A single brief episode — typically a short split around a power
// change — is transient.
function combustionCueEpisodes(candidates, top, config) {
  const cylinderOf = (candidate) => (Number.isFinite(candidate?.cylinder) ? candidate.cylinder : null);
  const intervals = candidates
    .filter((candidate) => candidate.type === top.type && cylinderOf(candidate) === cylinderOf(top))
    .map((candidate) => ({ start: candidate.startTime, end: candidate.endTime }))
    .filter((interval) => Number.isFinite(interval.start) && Number.isFinite(interval.end) && interval.end >= interval.start)
    .sort((a, b) => a.start - b.start);
  const episodes = [];
  intervals.forEach((interval) => {
    const last = episodes.at(-1);
    if (last && interval.start - last.end <= config.episodeGapSeconds) {
      last.end = Math.max(last.end, interval.end);
      return;
    }
    episodes.push({ start: interval.start, end: interval.end });
  });
  const longestSeconds = episodes.reduce((max, episode) => Math.max(max, episode.end - episode.start), 0);
  return {
    count: episodes.length,
    longestSeconds: Math.round(longestSeconds),
    systemic: episodes.length >= config.systemicMinEpisodes || longestSeconds >= config.sustainedEpisodeSeconds,
  };
}

function analyzeCombustionSignaturesForKeys(data, egtSourceKeys, chtSourceKeys) {
  const candidateWindowConfig = chtEgtPairingParameters.candidateWindow;
  const globalConfig = chtEgtPairingParameters.globalSplit;
  const coldConfig = chtEgtPairingParameters.coldCylinder;
  const ignitionConfig = chtEgtPairingParameters.ignitionCylinder;
  const injectorConfig = chtEgtPairingParameters.injector;
  const scoring = chtEgtPairingParameters.scoring;
  const egtKeys = egtSourceKeys.filter((key) => data.some((point) => Number.isFinite(point[key])));
  const chtKeys = chtSourceKeys.filter((key) => data.some((point) => Number.isFinite(point[key])));
  const pairedKeys = egtKeys.filter(key => chtKeys.includes(`cht${cylinderNumberFromKey(key)}`));
  if (pairedKeys.length < 2) {
    return { key: "combustion", score: null, note: "Need paired CHT and EGT channels", details: {} };
  }

  const windows = findStableEgtWindows(data);
  if (!windows.length) {
    return { key: "combustion", score: null, note: "This log does not include a steady-power segment with enough paired CHT and EGT data for the CHT/EGT pairing check.", details: { windows: 0 } };
  }
  // Settled-state gate: only windows whose lead-in horizon was also
  // mixture/power quiet count as evidence — everything else is transition
  // territory where thermal lag manufactures fake splits.
  const settledWindows = windows.filter((windowRef) => windowHasSettledContext(data, windowRef));
  if (!settledWindows.length) {
    return {
      key: "combustion",
      score: null,
      note: "This log has no mixture-settled segment long enough for the CHT/EGT pairing check (power or mixture was moving near every steady stretch).",
      details: { windows: windows.length, settledWindows: 0 },
    };
  }

  const candidates = [];
  const evaluatedWindows = [];
  settledWindows.forEach((windowRef) => {
    const window = windowRef.points.filter(engineWorkPoint);
    if (window.length < windowRef.points.length * candidateWindowConfig.minEngineWorkShare || window.length < candidateWindowConfig.minEngineWorkSamples) return;
    const movement = windowPowerMovement(window);
    if (!isWindowPowerStable(movement)) return;
    const hasPower = ["rpm", "map", "fuelFlow"].some(key => window.every(point => Number.isFinite(point[key])));
    if (!hasPower) return;
    const workRuns = continuousAnalysisRuns(windowRef.points, engineWorkPoint, chtEgtPairingParameters.coverage);
    if (workRuns.length !== 1 || workRuns[0].length !== windowRef.points.length) return;
    const usablePairs = pairedKeys.filter(key => window.every(point => Number.isFinite(point[key])
      && Number.isFinite(point[`cht${cylinderNumberFromKey(key)}`])));
    if (usablePairs.length < 2) return;
    evaluatedWindows.push({ ...windowRef, points: window, pairedKeys: usablePairs });
    const windowEgtKeys = usablePairs;
    const windowChtKeys = usablePairs.map(key => `cht${cylinderNumberFromKey(key)}`);
    const powerMovement = combustionPowerContext(data, windowRef, window, movement);

    const commonEgtChange = commonWindowChange(window, windowEgtKeys, { robust: true });
    const commonChtChange = commonWindowChange(window, windowChtKeys, { robust: true });
    const startTime = window[0].elapsedSeconds ?? windowRef.start;
    const endTime = window.at(-1).elapsedSeconds ?? windowRef.end;

    if (Number.isFinite(commonEgtChange) && Number.isFinite(commonChtChange)
      && commonEgtChange >= globalConfig.minEgtRiseF && commonChtChange <= globalConfig.maxChtDropF) {
      const fuelFlowThermal = fuelFlowThermalContext(data, windowRef, window, windowEgtKeys);
      const fuelFlowMovement = fuelFlowMovementSplitContext(powerMovement, commonEgtChange, commonChtChange);
      const fuelFlowContext = strongerFuelFlowContext(fuelFlowThermal, fuelFlowMovement);
      const fuelFlowExplainsSplit = fuelFlowContext.score >= chtEgtPairingParameters.fuelThermal.explainScore;
      const rawSuspicion = globalConfig.baseSuspicion
        + Math.max(0, commonEgtChange - globalConfig.minEgtRiseF) * globalConfig.egtWeight
        + Math.max(0, -commonChtChange + globalConfig.maxChtDropF) * globalConfig.chtWeight;
      candidates.push({
        type: fuelFlowExplainsSplit ? "globalMixture" : "globalIgnition",
        label: combustionLabel(fuelFlowExplainsSplit ? "globalMixture" : "globalIgnition"),
        cylinder: null,
        commonEgtChange,
        commonChtChange,
        startTime,
        endTime,
        ...movement,
        powerMovement,
        powerMovementScore: powerMovement.score,
        fuelFlowThermal: fuelFlowContext,
        fuelFlowThermalDirectional: fuelFlowThermal,
        fuelFlowMovement,
        rawSuspicion,
        suspicion: fuelFlowExplainsSplit
          ? Math.max(globalConfig.explainedFloor, rawSuspicion - globalConfig.explainedBaseReduction - fuelFlowContext.score * globalConfig.explainedScoreWeight)
          : rawSuspicion,
      });
    }

    usablePairs.forEach((egtKey) => {
      const cylinder = cylinderNumberFromKey(egtKey);
      const chtKey = `cht${cylinder}`;
      if (!windowChtKeys.includes(chtKey)) return;
      const egtChange = relativeWindowChange(window, egtKey, windowEgtKeys, { robust: true });
      const chtChange = relativeWindowChange(window, chtKey, windowChtKeys, { robust: true });
      if (!Number.isFinite(egtChange) || !Number.isFinite(chtChange)) return;

      const endEgt = windowEndMedian(window, egtKey);
      const endCht = windowEndMedian(window, chtKey);
      const endPeerEgt = windowPeerEndMedian(window, egtKey, windowEgtKeys, { robust: true });
      const base = {
        cylinder,
        egtKey,
        chtKey,
        egtChange,
        chtChange,
        endEgt,
        endCht,
        endPeerEgt,
        commonEgtChange,
        commonChtChange,
        startTime,
        endTime,
        ...movement,
        powerMovement,
        powerMovementScore: powerMovement.score,
      };

      const coldSeparation = Number.isFinite(endPeerEgt) && Number.isFinite(endEgt) ? endPeerEgt - endEgt : 0;
      // End-EGT cold check is peer-relative, not a fixed absolute: an absolute floor
      // makes every cylinder look cold on engines that simply cruise cooler. This
      // fires only when a cylinder ends well below its peers' end EGT.
      const endsColdVsPeers = Number.isFinite(endEgt) && Number.isFinite(endPeerEgt)
        && endEgt < endPeerEgt * coldConfig.maxEndEgtPeerShare;
      if ((egtChange <= coldConfig.maxRelativeEgtDropF || coldSeparation >= coldConfig.minColdSeparationF || endsColdVsPeers)
        && chtChange <= coldConfig.maxChtDropF) {
        candidates.push({
          ...base,
          type: "coldCylinder",
          label: combustionLabel("coldCylinder"),
          suspicion: coldConfig.baseSuspicion
            + Math.max(0, -egtChange + coldConfig.maxRelativeEgtDropF) * coldConfig.egtWeight
            + Math.max(0, coldSeparation - coldConfig.minColdSeparationF) * coldConfig.separationWeight
            + Math.max(0, -chtChange + coldConfig.maxChtDropF) * coldConfig.chtWeight,
        });
        return;
      }

      if (egtChange >= ignitionConfig.minRelativeEgtRiseF && chtChange <= ignitionConfig.maxRelativeChtDropF) {
        candidates.push({
          ...base,
          type: "ignitionCylinder",
          label: combustionLabel("ignitionCylinder"),
          chtCorroborated: true,
          suspicion: ignitionConfig.baseSuspicion
            + Math.max(0, egtChange - ignitionConfig.minRelativeEgtRiseF) * ignitionConfig.egtWeight
            + Math.max(0, -chtChange + ignitionConfig.maxRelativeChtDropF) * ignitionConfig.chtWeight,
        });
      } else if (egtChange >= injectorConfig.minRelativeEgtRiseF && chtChange >= injectorConfig.minRelativeChtRiseF) {
        const rawSuspicion = injectorConfig.baseSuspicion
          + Math.max(0, egtChange - injectorConfig.minRelativeEgtRiseF) * injectorConfig.egtWeight
          + Math.max(0, chtChange - injectorConfig.minRelativeChtRiseF) * injectorConfig.chtWeight;
        const powerConfoundPenalty = powerMovement.score >= injectorConfig.confoundScore ? injectorConfig.confoundBasePenalty + powerMovement.score * injectorConfig.confoundScoreWeight : 0;
        candidates.push({
          ...base,
          type: "injectorRop",
          label: combustionLabel("injectorRop"),
          suspicion: Math.max(injectorConfig.suspicionFloor, rawSuspicion - powerConfoundPenalty),
          rawSuspicion,
        });
      } else if (egtChange <= injectorConfig.maxRelativeEgtDropF && chtChange <= injectorConfig.maxRelativeChtDropF) {
        const rawSuspicion = injectorConfig.baseSuspicion
          + Math.max(0, -egtChange + injectorConfig.maxRelativeEgtDropF) * injectorConfig.egtWeight
          + Math.max(0, -chtChange + injectorConfig.maxRelativeChtDropF) * injectorConfig.chtWeight;
        const powerConfoundPenalty = powerMovement.score >= injectorConfig.confoundScore ? injectorConfig.confoundBasePenalty + powerMovement.score * injectorConfig.confoundScoreWeight : 0;
        candidates.push({
          ...base,
          type: "injectorLop",
          label: combustionLabel("injectorLop"),
          suspicion: Math.max(injectorConfig.suspicionFloor, rawSuspicion - powerConfoundPenalty),
          rawSuspicion,
        });
      } else if (egtChange >= ignitionConfig.softEgtRiseF
        && chtChange > ignitionConfig.maxRelativeChtDropF
        && chtChange < injectorConfig.minRelativeChtRiseF) {
        // Clear single-cylinder EGT rise with CHT roughly flat: most often early
        // plug/lead fouling (CHT lags EGT) or a slightly lean cylinder. Lower
        // confidence than the corroborated split, and power-confound-penalized.
        const rawSuspicion = ignitionConfig.softBaseSuspicion
          + Math.max(0, egtChange - ignitionConfig.softEgtRiseF) * ignitionConfig.softEgtWeight;
        const powerConfoundPenalty = powerMovement.score >= ignitionConfig.softConfoundScore
          ? ignitionConfig.softConfoundBasePenalty + powerMovement.score * ignitionConfig.softConfoundScoreWeight
          : 0;
        candidates.push({
          ...base,
          type: "ignitionCylinder",
          label: "Single-cylinder EGT rise",
          chtCorroborated: false,
          suspicion: Math.max(ignitionConfig.softSuspicionFloor, rawSuspicion - powerConfoundPenalty),
          rawSuspicion,
        });
      }
    });
  });

  const steadyEvidence = summarizeSteadyEvidence(evaluatedWindows, pairedKeys);
  const coverageConfig = chtEgtPairingParameters.coverage;
  const evaluatedSeconds = Math.min(...steadyEvidence.coverageByCylinder.map(item => item.seconds));
  const reasonCodes = evaluatedSeconds < coverageConfig.minSeconds ? ["insufficient-paired-coverage"]
    : evaluatedSeconds < coverageConfig.fullSeconds ? ["limited-paired-coverage"] : [];
  const coverage = { status: evaluatedSeconds >= coverageConfig.minSeconds ? "evaluated" : "unavailable",
    evaluatedSeconds, coverageByCylinder: steadyEvidence.coverageByCylinder };
  const confidence = { label: reasonCodes.length ? "Low" : "Medium", reasonCodes };

  // Settle-persistence screen: a shift that re-converges once temperatures
  // settle was thermal lag (fast EGT, slow CHT), not a combustion change —
  // drop it. A shift whose settle horizon runs past the end of the log is
  // reported at low confidence only.
  const settleChecked = [];
  candidates.forEach((candidate) => {
    const settle = candidateSettleVerdict(data, candidate, egtKeys);
    if (settle.verdict === "reconverged") return;
    if (settle.verdict === "unconfirmed") {
      settleChecked.push({
        ...candidate,
        settleUnconfirmed: true,
        suspicion: Math.min(candidate.suspicion, chtEgtPairingParameters.settledContext.unconfirmedSuspicionCap),
      });
      return;
    }
    settleChecked.push({ ...candidate, settledShiftF: settle.settledShiftF });
  });

  if (!settleChecked.length) {
    return {
      key: "combustion",
      score: coverage.status === "evaluated" ? 100 : null,
      note: coverage.status === "evaluated"
        ? `${evaluatedWindows.length} settled window${evaluatedWindows.length === 1 ? "" : "s"} analyzed, paired CHT/EGT clear`
        : "Need more continuous paired CHT/EGT data with recorded steady power",
      details: { windows: windows.length, settledWindows: evaluatedWindows.length, candidates: [], steadyEvidence, coverage, confidence },
    };
  }

  const ranked = dedupeCombustionCandidates(settleChecked);
  const top = ranked[0];
  const cueEpisodes = combustionCueEpisodes(settleChecked, top, scoring.transient);
  // A shift that survived the settle horizon is systemic evidence in itself —
  // the settled-state check already proved it outlived thermal lag.
  const settledPersisted = Number.isFinite(top.settledShiftF)
    && Math.abs(top.settledShiftF) >= scoring.transient.systemicSettledShiftF;
  const systemicCue = cueEpisodes.systemic || settledPersisted;
  let score = clampScore(100 - Math.min(scoring.suspicionCap, top.suspicion));
  // A transient cue (one brief episode) is context, not an alarm: floor its
  // score into the Watch band so an isolated appearance can never read red.
  const transientCapped = !systemicCue && score < scoring.transient.cappedScore;
  if (transientCapped) score = scoring.transient.cappedScore;
  const unconfirmedNote = top.settleUnconfirmed === true ? " (near log end, unconfirmed)" : "";
  const note = score < scoring.reviewScoreThreshold
    ? `${top.label}${Number.isFinite(top.cylinder) ? ` on cyl ${top.cylinder}` : ""}${transientCapped ? " (isolated appearance)" : ""}${unconfirmedNote}`
    : `${settledWindows.length} settled window${settledWindows.length === 1 ? "" : "s"} analyzed, paired CHT/EGT clear`;
  return {
    key: "combustion",
    score,
    note,
    details: {
      windows: windows.length,
      settledWindows: settledWindows.length,
      top,
      candidates: ranked,
      meta: combustionEvidenceMeta(top),
      steadyEvidence,
      coverage,
      confidence,
      cueEpisodes: { ...cueEpisodes, systemic: systemicCue },
      transientCapped,
    },
  };
}

function analyzeCombustionSignaturesResult(data, options = {}) {
  const descriptors = Array.isArray(options.engineDescriptors) ? options.engineDescriptors : null;
  if (descriptors && descriptors.length > 1) {
    const perEngine = descriptors.map((engine) => {
      const egtSourceKeys = engineCylinderKeys(engine, "egt");
      const chtSourceKeys = engineCylinderKeys(engine, "cht");
      const projected = projectFlightDataForEngine(data, engine);
      const result = labelEngineMetricResult(analyzeCombustionSignaturesForKeys(projected, egtSourceKeys, chtSourceKeys), descriptors);
      return {
        engineKey: engine.engineKey ?? engine.id ?? `engine${(engine.index ?? 0) + 1}`,
        label: engine.label,
        shortLabel: engine.shortLabel ?? engine.label,
        result,
      };
    }).filter(Boolean);
    const scoredEngines = perEngine.filter((entry) => Number.isFinite(entry.result.score));
    if (scoredEngines.length < 2) return combineEngineMetricResults(perEngine);
    if (scoredEngines.length >= 2) {
      const worst = scoredEngines.reduce((acc, entry) => {
        if (acc === null) return entry;
        return entry.result.score < acc.result.score ? entry : acc;
      }, null);
      const score = scoredEngines.reduce((acc, entry) => Math.min(acc, entry.result.score), 100);
      const mergedCandidates = perEngine.flatMap((entry) =>
        (entry.result.details?.candidates ?? []).map((candidate) => ({
          ...candidate,
          engineKey: entry.engineKey,
          engineLabel: entry.label,
          engineShortLabel: entry.shortLabel,
          label: entry.shortLabel ? `${entry.shortLabel}: ${candidate.label}` : candidate.label,
        })),
      ).sort((a, b) => (b.suspicion ?? 0) - (a.suspicion ?? 0));
      const top = mergedCandidates[0] ?? worst.result.details?.top ?? null;
      // top.cylinder is a source channel number (1-16 across both engines); the note
      // must show the per-engine display number, so remap it the same way the finding
      // title does.
      const topDisplayCylinder = top && Number.isFinite(top.cylinder)
        ? engineInfoForSourceNumber(top.cylinder, descriptors).cylinder?.number ?? top.cylinder
        : null;
      const topEngineCapped = top?.engineKey
        ? perEngine.find((entry) => entry.engineKey === top.engineKey)?.result.details?.transientCapped === true
        : worst.result.details?.transientCapped === true;
      const note = top && score < chtEgtPairingParameters.scoring.reviewScoreThreshold
        ? `${top.label}${Number.isFinite(topDisplayCylinder) ? ` on cyl ${topDisplayCylinder}` : ""}${topEngineCapped ? " (isolated appearance)" : ""}`
        : `${perEngine.reduce((sum, entry) => sum + (entry.result.details?.settledWindows ?? entry.result.details?.windows ?? 0), 0)} settled windows analyzed, paired CHT/EGT clear`;
      return {
        key: "combustion",
        score,
        note,
        details: {
          ...worst.result.details,
          top,
          candidates: mergedCandidates,
          engines: perEngine.map((entry) => ({
            engineKey: entry.engineKey,
            label: entry.label,
            shortLabel: entry.shortLabel,
            score: entry.result.score,
            note: entry.result.note,
            details: entry.result.details,
          })),
          worstEngineKey: worst.engineKey,
        },
      };
    }
  }
  return analyzeCombustionSignaturesForKeys(data, cylinderKeys("egt"), cylinderKeys("cht"));
}

export function chtEgtPairingEvaluationState(metric, engineKey = null) {
  if (engineKey) {
    const entry = metric?.details?.engines?.find((candidate) => candidate.engineKey === engineKey);
    metric = entry?.result ?? entry;
  }
  const details = metric?.details ?? {};
  const settledWindows = details.settledWindows;
  const hasSettledWindowCount = Number.isFinite(settledWindows);
  const evaluated = Number.isFinite(metric?.score)
    && details.status !== "not-applicable"
    && (!hasSettledWindowCount || settledWindows > 0);
  const reason = evaluated
    ? ""
    : String(details.reason ?? metric?.note ?? "No eligible settled CHT/EGT pairing evidence was available.").trim();
  return Object.freeze({
    evaluated,
    reason,
  });
}

// Every cylinder that logs paired CHT+EGT this flight, for the Overview pairing tiles
// (the detector emits records only for FLAGGED cylinders, so clean cylinders need a
// roster). Twin-aware: per-engine source numbers + engineShortLabel for grouping.
export function chtEgtPairingRoster(data, { engineDescriptors } = {}) {
  const points = Array.isArray(data) ? data : [];
  const descriptors = Array.isArray(engineDescriptors) ? engineDescriptors : [];
  const hasKey = (key) => points.some((point) => Number.isFinite(point[key]));
  const entriesForChtKeys = (chtKeys, engineShortLabel) => chtKeys
    .filter((chtKey) => hasKey(chtKey) && hasKey(`egt${cylinderNumberFromKey(chtKey)}`))
    .map((chtKey) => ({ cylinder: cylinderNumberFromKey(chtKey), ...(engineShortLabel ? { engineShortLabel } : {}) }))
    .filter((entry) => Number.isFinite(entry.cylinder));
  if (descriptors.length > 1) {
    // Carry the descriptor DISPLAY number per cylinder (engines.js numbers every
    // cylinder that has either channel) so the tile label matches the score note even
    // when a paired-channel cylinder is missing one channel and the roster is short.
    return descriptors.flatMap((engine) => {
      const engineShortLabel = engine.shortLabel ?? engine.label ?? null;
      return (Array.isArray(engine.cylinders) ? engine.cylinders : [])
        .filter((cyl) => cyl?.chtKey && hasKey(cyl.chtKey) && hasKey(`egt${cylinderNumberFromKey(cyl.chtKey)}`))
        .map((cyl) => {
          const cylinder = cylinderNumberFromKey(cyl.chtKey);
          return { cylinder, displayNumber: Number.isFinite(cyl.number) ? cyl.number : cylinder, engineKey: engine.engineKey ?? engine.id, engineShortLabel };
        })
        .filter((entry) => Number.isFinite(entry.cylinder));
    });
  }
  return entriesForChtKeys(cylinderKeys("cht"), null);
}

// Per-cylinder CHT + EGT trace (with peer averages) for the Overview CHT/EGT-pairing
// overlay. The same engine-work gate the score uses spans the whole run; CHT and EGT
// are smoothed independently, and the per-sample peer averages (mean of the OTHER
// cylinders' CHT / EGT) let the renderer show the focused pair diverging from its
// peers. Peers belong to the same engine as the focused cylinder.
// Returns an empty-but-valid trace (never null) when
// the cylinder lacks paired CHT+EGT.
export function chtEgtPairingTraceForKey(data, chtKey, { engineDescriptors = [] } = {}) {
  const cylinder = cylinderNumberFromKey(chtKey);
  const egtKey = Number.isFinite(cylinder) ? `egt${cylinder}` : null;
  const empty = {
    key: chtKey, egtKey, cylinder: Number.isFinite(cylinder) ? cylinder : null,
    label: Number.isFinite(cylinder) ? `Cyl ${cylinder}` : "Cylinder",
    samples: [], hasCht: false, hasEgt: false, chtExtent: null, egtExtent: null, peerCount: 0, sampleCount: 0,
  };
  if (!Number.isFinite(cylinder)) return empty;
  const owner = engineInfoForSourceNumber(cylinder, engineDescriptors).engine;
  if (engineDescriptors.length > 1 && !owner) return empty;
  const scopedData = owner ? projectFlightDataForEngine(data, owner) : data;
  const analysisData = elapsedAnchoredSampleForAnalysis(Array.isArray(scopedData) ? scopedData : [], 9000);
  const chtKeys = cylinderKeys("cht").filter((key) => analysisData.some((point) => Number.isFinite(point[key])));
  const egtKeys = cylinderKeys("egt").filter((key) => analysisData.some((point) => Number.isFinite(point[key])));
  let rows = analysisData
    .filter(engineWorkPoint)
    .map((point) => ({
      t: point.elapsedSeconds,
      cht: point[chtKey],
      egt: point[egtKey],
      peerCht: peerAverage(point, chtKeys, chtKey),
      peerEgt: peerAverage(point, egtKeys, egtKey),
    }))
    .filter((row) => Number.isFinite(row.t) && (Number.isFinite(row.cht) || Number.isFinite(row.egt)));
  // 3-point median smooth on CHT and EGT independently (each guarded for gaps).
  rows = rows.map((row, index) => {
    const next = { ...row };
    for (const field of ["cht", "egt"]) {
      const prev = rows[index - 1]?.[field];
      const after = rows[index + 1]?.[field];
      if (Number.isFinite(prev) && Number.isFinite(row[field]) && Number.isFinite(after)) {
        next[field] = [prev, row[field], after].slice().sort((a, b) => a - b)[1];
      }
    }
    return next;
  });
  const stride = Math.max(1, Math.ceil(rows.length / 600));
  const kept = rows.filter((_, index) => index % stride === 0);
  if (rows.length && kept[kept.length - 1] !== rows[rows.length - 1]) kept.push(rows[rows.length - 1]);
  const chtValues = kept.map((row) => row.cht).filter(Number.isFinite);
  const egtValues = kept.map((row) => row.egt).filter(Number.isFinite);
  return {
    key: chtKey,
    egtKey,
    cylinder,
    label: engineScopedCylinderLabel(cylinder, engineDescriptors, "Cyl", { style: "narrative" }),
    samples: kept.map((row) => ({
      t: row.t,
      cht: Number.isFinite(row.cht) ? row.cht : null,
      egt: Number.isFinite(row.egt) ? row.egt : null,
      peerCht: Number.isFinite(row.peerCht) ? row.peerCht : null,
      peerEgt: Number.isFinite(row.peerEgt) ? row.peerEgt : null,
    })),
    hasCht: chtValues.length > 0,
    hasEgt: egtValues.length > 0,
    chtExtent: chtValues.length ? extentFromValues(chtValues) : null,
    egtExtent: egtValues.length ? extentFromValues(egtValues) : null,
    peerCount: Math.max(0, chtKeys.length - 1),
    sampleCount: kept.length,
  };
}

export function analyzeCombustionSignatures(data, options = {}) {
  return withEngineCoverageConfidence(analyzeCombustionSignaturesResult(data, options));
}
